import { Suspense, useEffect } from 'react';
import { GAMES, type GameId } from '@sf72/shared';
import { useMatch } from './store/match.js';
import { boardFor } from './games/registry.js';
import { PlayerBar } from './ui/PlayerBar.js';
import { Controls } from './ui/Controls.js';
import { ResultCard } from './ui/ResultCard.js';
import { ConnectionBanner, EmojiLayer, ErrorToast } from './ui/Overlays.js';
import { Centered, Hint, Screen, Spinner } from './ui/primitives.js';
import { closeApp, getStartParam, isTelegram } from './telegram.js';
import { unlockAudio } from './sound.js';

/**
 * Which match to open.
 *
 * A board is reached two ways: the bot's launch button, which puts the id in
 * the query string, and a deep link, where Telegram hands it over as
 * `start_param`.
 */
function targetMatchId(): string | null {
  const fromQuery = new URLSearchParams(window.location.search).get('match');
  if (fromQuery) return fromQuery;

  const startParam = getStartParam();
  if (startParam?.startsWith('m_')) return startParam.slice(2);
  return null;
}

export function App() {
  const status = useMatch((s) => s.status);
  const match = useMatch((s) => s.match);
  const state = useMatch((s) => s.state);
  const yourSeat = useMatch((s) => s.yourSeat);
  const legalMoves = useMatch((s) => s.legalMoves);
  const submitting = useMatch((s) => s.submitting);
  const error = useMatch((s) => s.error);
  const skewMs = useMatch((s) => s.clockSkewMs);
  const connect = useMatch((s) => s.connect);
  const disconnect = useMatch((s) => s.disconnect);
  const join = useMatch((s) => s.join);
  const sendMove = useMatch((s) => s.sendMove);

  useEffect(() => {
    connect();
    return disconnect;
  }, [connect, disconnect]);

  useEffect(() => {
    if (status !== 'ready') return;
    const matchId = targetMatchId();
    if (matchId) join(matchId);
  }, [status, join]);

  // Audio needs a gesture before it will play; the first tap anywhere counts.
  useEffect(() => {
    const handler = (): void => unlockAudio();
    window.addEventListener('pointerdown', handler, { once: true });
    return () => window.removeEventListener('pointerdown', handler);
  }, []);

  if (!isTelegram && !import.meta.env.DEV) {
    return (
      <Centered>
        <div className="text-2xl">🎮</div>
        <h1 className="text-lg font-semibold">SF72 Game</h1>
        <Hint>Bu sahifa Telegram ichida ochilishi kerak.</Hint>
      </Centered>
    );
  }

  if (!match) {
    const matchId = targetMatchId();
    return (
      <Centered>
        {matchId && !error ? (
          <>
            <Spinner />
            <Hint>O‘yin yuklanyapti…</Hint>
          </>
        ) : matchId && error ? (
          // Without this the app spun forever on a match that no longer exists:
          // the server does say so, but the message was only rendered inside the
          // branch that needs a match to render at all.
          <>
            <div className="text-2xl">🙈</div>
            <Hint>{error}</Hint>
            <button
              type="button"
              onClick={closeApp}
              className="mt-2 text-[15px] text-link"
            >
              Botga qaytish
            </button>
          </>
        ) : (
          <>
            <div className="text-2xl">🎮</div>
            <h1 className="text-lg font-semibold">SF72 Game</h1>
            <Hint>O‘yinni botdan boshlang, keyin bu yerda davom etadi.</Hint>
            <button
              type="button"
              onClick={closeApp}
              className="mt-2 text-[15px] text-link"
            >
              Botga qaytish
            </button>
          </>
        )}
        <ConnectionBanner />
      </Centered>
    );
  }

  const game = GAMES[match.gameId as GameId];
  const BoardComponent = boardFor(match.gameId as GameId);
  const canMove =
    match.status === 'active' &&
    yourSeat !== null &&
    !submitting &&
    (match.turnSeat === null || match.turnSeat === yourSeat);

  const names: string[] = [];
  for (const player of match.players) names[player.seat] = player.user.firstName;

  return (
    <Screen>
      <ConnectionBanner />
      <ErrorToast />
      <EmojiLayer />

      <PlayerBar match={match} yourSeat={yourSeat} skewMs={skewMs} />

      <div className="flex flex-1 flex-col">
        {BoardComponent ? (
          <Suspense
            fallback={
              <div className="flex flex-1 items-center justify-center">
                <Spinner />
              </div>
            }
          >
            <BoardComponent
              state={state as never}
              yourSeat={yourSeat}
              turnSeat={match.turnSeat}
              legalMoves={legalMoves as never}
              canMove={canMove}
              onMove={sendMove as never}
              names={names}
            />
          </Suspense>
        ) : (
          <Centered>
            <div className="text-2xl">{game.emoji}</div>
            <Hint>{game.name} hali Mini App'da chizilmagan.</Hint>
          </Centered>
        )}
      </div>

      <ResultCard />
      <Controls />
    </Screen>
  );
}
